/**
 * This file defines a set of reusable metadata consts w/ EC dependencies, and is bundled in a lazy-load chunk.
 * Make sure you don't import this file in the main chunk.
 */
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';

import { fileShareTextareaBase, parameterizableMetadataBase } from '../linked-service/azure-storage-shared-fields';
import { azureResourceSelectMethodOptions, yesOrNoOptions } from './metadata-consts';
import { format } from 'app/shared/utils/string.util';
import { PrimaryEndpoints } from 'app/extensibility/metadatas/control/azure-storage-account-select-metadata';
import { tableLockInfoLink } from 'app/shared/constants/links.const';

const useBulkInsertTableLockMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'radio',
        bind: 'sqlWriterUseTableLock',
        defaultValue: false,
        label: stringResources.Dataset_UseBulkInsertTableLock,
        options: yesOrNoOptions,
        description: stringResources.Dataset_UseBulkInsertTableLock_info + '<a target="_blank" href="' + tableLockInfoLink + '">' + stringResources.LearnMore + '</a>',
        escapeDescription: false
    },
    dynamicContentTextClass: 'input-spaced'
};

const sqlServerUseBulkInsertTableLockMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'radio',
        bind: 'sqlWriterUseTableLock',
        defaultValue: true,
        label: stringResources.Dataset_UseBulkInsertTableLock,
        options: yesOrNoOptions,
        description: stringResources.Dataset_UseBulkInsertTableLock_info + '<a target="_blank" href="' + tableLockInfoLink + '">' + stringResources.LearnMore + '</a>',
        escapeDescription: false
    },
    dynamicContentTextClass: 'input-spaced'
};

export function getSqlWriteBehavior(isSqlServer: boolean): ControlMetadata {
    return {
        type: 'radio-switch',
        label: stringResources.ModelName_WriteBehavior,
        bind: 'writeBehavior',
        isParameterizable: true,
        options: [
            {
                value: 'insert',
                label: stringResources.WriteBehavior_Option_Insert
            },
            {
                value: 'upsert',
                label: stringResources.WriteBehavior_Option_Upsert
            },
            {
                value: 'storedProcedure',
                label: stringResources.StoredProcedure
            }
        ],
        defaultValue: 'insert',
        cases: [
            {
                caseValue: 'insert',
                children: []
            },
            {
                caseValue: 'upsert',
                children: [
                    {
                        type: 'sql-upsert',
                        bind: {
                            useTempDB: 'writeBehaviorUseTempDB',
                            interimSchemaName: 'writeBehaviorInterimSchemaName',
                            keys: 'writeBehaviorKeys'
                        },
                        hasTempDB: true,
                        hasInterimSchemaName: true,
                        keyColumnsAttributeName: 'writeBehaviorKeys',
                        parameterizable: true
                    }
                ]
            },
            {
                caseValue: 'storedProcedure',
                children: [{
                    type: 'stored-procedure',
                    bind: {
                        selectionValue: 'sqlWriterStoredProcedureName',
                        sqlSinkWriterTableType: 'sqlWriterTableType',
                        sinkSpTableTypeParameterName: 'storedProcedureTableTypeParameterName',
                        parameterConfigs: 'storedProcedureParameters'
                    },
                    isSink: true
                }]
            }
        ],
        sharedControls: [
            {
                caseValues: ['insert', 'upsert'],
                controls: isSqlServer ? [sqlServerUseBulkInsertTableLockMetadata] : [useBulkInsertTableLockMetadata]
            }
        ]
    };
}

const accountNameBase: ControlMetadata = {
    type: 'textbox',
    bind: 'accountName',
    label: stringResources.ModelName_StorageAccountName,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' },
        {
            type: 'pattern',
            pattern: '^[a-z0-9-]{3,}$|^[*]{10}$', // for secondary endpoints, the format is `${accountName}-secondary`, which would include '-' (invalid char) & may exceeds 24 length limit
            errorMessage: stringResources.Validation_StorageAccountName,
        }
    ],
    isLabelVertical: true
};

const endpointSuffixBase: ControlMetadata = {
    type: 'textbox',
    bind: 'endpointSuffix',
    label: stringResources.ModelName_EndpointSuffix,
    required: false,
    isLabelVertical: true
};

const accountKeyPasswordBase: ControlMetadata = {
    type: 'password',
    label: stringResources.ModelName_StorageAccountKey,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};

export function accountKeyConnectionStringSetting(serviceType?: PrimaryEndpoints, showFileShare?: boolean, enableFirstClass?: boolean): ControlMetadata {
    let endpoint = '';
    const storageReservedKeys: string[] = ['AccountName', 'AccountKey', 'EndpointSuffix', 'DefaultEndpointsProtocol'];
    switch (serviceType) {
        case 'blob':
            endpoint = stringResources.BlobEndpoint;
            storageReservedKeys.push('BlobEndpoint');
            break;
        case 'file':
            endpoint = stringResources.FileEndpoint;
            storageReservedKeys.push('FileEndpoint');
            break;
        case 'table':
            endpoint = stringResources.TableEndpoint;
            storageReservedKeys.push('TableEndpoint');
            break;
        default:
            endpoint = '';
    }
    return {
        type: 'select-button-switch',
        required: true,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.ModelName_ConnectionString,
                value: false
            },
            {
                label: stringResources.AzureKeyVault_DisplayText,
                value: true,
                hideAKVOptionIfNotChosen: false
            }
        ],
        bind: 'enableAccountAKV',
        isNotRelatedToCredential: true,
        defaultValue: false,
        cases: [
            {
                caseValue: true,
                children: [
                    {
                        type: 'azure-key-vault',
                        enableAKVParameterization: enableFirstClass,
                        bind: 'referedAccountAKV',
                        isLabelVertical: true,
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureStorage_DisplayText),
                            'DefaultEndpointsProtocol=https;AccountName=<accountname>;AccountKey=<accountkey>;EndpointSuffix=<endpointSuffix>')
                    },
                    parameterizableMetadataBase(fileShareTextareaBase, enableFirstClass, {
                        hide: !showFileShare
                    })
                ]
            },
            {
                caseValue: false,
                children: [{
                    type: 'radio-switch',
                    nested: 1,
                    label: stringResources.AccountSelectionMethodLabel,
                    description: stringResources.AzureStorage_SelectMethodDescription,
                    options: azureResourceSelectMethodOptions,
                    isLabelVertical: true,
                    defaultValue: '{accountName != null || parameterized ? "manual" : "azure"}',
                    cases: [
                        {
                            caseValue: 'azure',
                            children: [
                                {
                                    type: 'azure-storage-account-select',
                                    bind: {
                                        accountName: 'accountName',
                                        accountKey: 'accountKey',
                                        scope: '_accountResourceID',
                                        endpointSuffix: 'endpointSuffix',
                                        primaryEndpoint: 'primaryEndpoint',
                                        fileShare: 'fileShare'
                                    },
                                    showPEStatus: true,
                                    showFileShare,
                                    serviceType,
                                    enableAKVParameterization: true,
                                    validators: !showFileShare ? {
                                        accountName: [
                                            { type: 'required', errorMessage: '' }
                                        ],
                                        accountKey: [
                                            { type: 'required', errorMessage: '' }
                                        ]
                                    } : {
                                        accountName: [
                                            { type: 'required', errorMessage: '' }
                                        ],
                                        accountKey: [
                                            { type: 'required', errorMessage: '' }
                                        ],
                                        fileShare: [
                                            { type: 'required', errorMessage: '' }
                                        ]
                                    }
                                }
                            ]
                        },
                        {
                            caseValue: 'manual',
                            children: [
                                parameterizableMetadataBase(accountNameBase, enableFirstClass),
                                {
                                    type: 'dynamic-akv-switcher',
                                    hideAKVSwitcherInConnStrProps: true,
                                    bind: 'accountKey',
                                    label: stringResources.ModelName_StorageAccountKey,
                                    enableAKVParameterization: enableFirstClass,
                                    child: parameterizableMetadataBase(accountKeyPasswordBase, enableFirstClass, {
                                        attribute: 'accountKey',
                                        disableAddParameter: true
                                    }),
                                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureStorage_DisplayText),
                                        stringResources.ModelName_StorageAccountKey)
                                },
                                {
                                    type: 'checkbox-switch',
                                    labelPosition: 'left',
                                    bind: 'isPartitionedDns',
                                    description: stringResources.partitionedDnsTooltip,
                                    label: stringResources.enablePartitionedDns,
                                    defaultValue: false,
                                    cases: [
                                        {
                                            caseValue: false,
                                            children: [
                                                {
                                                    type: 'parameterizable',
                                                    attribute: 'endpointSuffix',
                                                    child: endpointSuffixBase
                                                }
                                            ]
                                        },
                                        {
                                            caseValue: true,
                                            children: [
                                                {
                                                    type: 'parameterizable',
                                                    attribute: 'primaryEndpoint',
                                                    child: {
                                                        type: 'textbox',
                                                        bind: 'primaryEndpoint',
                                                        label: endpoint,
                                                        required: true,
                                                        isLabelVertical: true,
                                                        validators: [
                                                            { type: 'required', errorMessage: '' }
                                                        ]
                                                    }
                                                }
                                            ]
                                        }
                                    ]
                                },
                                parameterizableMetadataBase(endpointSuffixBase, enableFirstClass, {
                                    hide: true
                                }),
                                parameterizableMetadataBase(fileShareTextareaBase, enableFirstClass, {
                                    hide: !showFileShare
                                }),
                                {
                                    type: 'private-endpoint-status'
                                }
                            ]
                        }
                    ]
                },
                {
                    type: 'key-val-list',
                    bind: 'additionalFields',
                    label: stringResources.AdditionalConnectionStringFields,
                    reservedKeys: storageReservedKeys
                }]
            }
        ]
    };
}
